import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthSessionService } from '../../auth/services/auth-session.service';
import { SubdomainService } from '../../../core/services/subdomain.service';
import { CreateDutyPayload, UpdateDutyPayload } from '../../home/components/add-duty-dialog/add-duty-dialog.component';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { UserSharedService } from './user-shared.service';
import { HomeDataService } from '../../home/services/home-data.service';
import {
  FamilyEntitlement,
  PaymentAddon,
  PaymentPlan,
  PaymentsConfig,
} from '../models/payment-plan.model';

@Injectable({ providedIn: 'root' })
export class UserService {
  #http = inject(HttpClient);
  #session = inject(AuthSessionService);
  #userShared = inject(UserSharedService);
  #subdomain = inject(SubdomainService);
  #homeData = inject(HomeDataService);
  #queryClient = inject(QueryClient);

  user = computed(() => this.#user.data());
  /** Parent XP pool remaining balance (duties draw from this). */
  experiencePoolBalance = computed(
    () => this.user()?.experiencePool?.currentBalance ?? null,
  );
  products = computed(() => this.productsQuery.data());
  plans = computed(() => this.plansQuery.data() ?? []);
  entitlement = computed(() => this.entitlementQuery.data());
  paymentsConfig = computed(() => this.paymentsConfigQuery.data() ?? null);
  paymentsEnabled = computed(() =>
    Boolean(this.paymentsConfig()?.paymentsEnabled),
  );
  quotasEnforced = computed(() =>
    Boolean(this.paymentsConfig()?.quotasEnforced),
  );
  addonsEnabled = computed(() =>
    Boolean(this.paymentsConfig()?.addonsEnabled),
  );
  addons = computed(() => this.addonsQuery.data() ?? []);
  /** CARE+ can buy boost packs when addons flag is on. */
  canPurchaseAddons = computed(
    () =>
      this.addonsEnabled() &&
      this.isQuotaCapabilityAllowed('addons.purchase'),
  );
  /** Voice input in AI assistant (mobile). Off on BASE when payments UX is on. */
  speechToTextAllowed = computed(() => {
    if (!this.paymentsEnabled()) {
      return true;
    }
    const limit = this.entitlement()?.scopes?.['ai.speech_to_text'];
    return limit !== undefined && limit !== 0;
  });
  /**
   * Parent `/patrol` dashboard. Matches gateway `assertCapability('patrol.access')`:
   * only gated when quota enforcement is on.
   */
  patrolAccessAllowed = computed(() => this.isQuotaCapabilityAllowed('patrol.access'));

  /**
   * Plan capability when quota enforcement is on (same as gateway assertCapability).
   * Unlimited is a negative limit; 0 / missing is off.
   */
  isQuotaCapabilityAllowed(scopeKey: string): boolean {
    if (!this.quotasEnforced()) {
      return true;
    }
    const limit = this.entitlement()?.scopes?.[scopeKey];
    return limit !== undefined && limit !== 0;
  }

  quotaScopeLimit(scopeKey: string): number | null {
    const limit = this.entitlement()?.scopes?.[scopeKey];
    return typeof limit === 'number' && Number.isFinite(limit) ? limit : null;
  }
  settings = computed(() => this.#userShared.settings());

  isLoading = computed(() => this.#user.isLoading());

  #familySession = computed(
    () =>
      this.#session.isAuthenticated() && this.#subdomain.isFamilySubdomain(),
  );

  reloadProfile(): Promise<unknown> {
    return this.#user.refetch();
  }

  async invalidateFamilyKids(): Promise<void> {
    await this.#queryClient.invalidateQueries({ queryKey: ['family'] });
  }

  async invalidatePayments(): Promise<void> {
    await Promise.all([
      this.#queryClient.invalidateQueries({ queryKey: ['payment-config'] }),
      this.#queryClient.invalidateQueries({ queryKey: ['payment-plans'] }),
      this.#queryClient.invalidateQueries({ queryKey: ['payment-entitlement'] }),
      this.#queryClient.invalidateQueries({ queryKey: ['payment-addons'] }),
      this.#queryClient.invalidateQueries({ queryKey: ['products'] }),
      this.#user.refetch(),
    ]);
  }

  findAddon(code: string): PaymentAddon | undefined {
    const normalized = code.trim().toUpperCase();
    return this.addons().find((a) => a.code.toUpperCase() === normalized);
  }

  #user = injectQuery(() => ({
    queryKey: ['user'],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<AuthGen.User>(`${environment.baseUrl}/user/parent`)
          .pipe(take(1)),
      ),
    enabled: this.#familySession(),
  }));

  productsQuery = injectQuery(() => ({
    queryKey: ['products'],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http.get<any>(`${environment.baseUrl}/payments/products`),
      ),
    enabled: false,
  }));

  paymentsConfigQuery = injectQuery(() => ({
    queryKey: ['payment-config'],
    retry: 1,
    queryFn: async (): Promise<PaymentsConfig> => {
      try {
        return await lastValueFrom(
          this.#http.get<PaymentsConfig>(
            `${environment.baseUrl}/payments/config`,
          ),
        );
      } catch {
        return {
          paymentsEnabled: false,
          quotasEnforced: false,
          addonsEnabled: false,
        };
      }
    },
    enabled: this.#familySession(),
    staleTime: 30_000,
  }));

  plansQuery = injectQuery(() => ({
    queryKey: ['payment-plans'],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http.get<PaymentPlan[]>(
          `${environment.baseUrl}/payments/plans`,
        ),
      ),
    enabled: this.#familySession() && this.paymentsEnabled(),
  }));

  entitlementQuery = injectQuery(() => ({
    queryKey: ['payment-entitlement'],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http.get<FamilyEntitlement>(
          `${environment.baseUrl}/payments/entitlement`,
        ),
      ),
    enabled: this.#familySession() && this.paymentsEnabled(),
  }));

  addonsQuery = injectQuery(() => ({
    queryKey: ['payment-addons'],
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http.get<PaymentAddon[]>(`${environment.baseUrl}/payments/addons`),
      ),
    enabled:
      this.#familySession() &&
      this.paymentsEnabled() &&
      this.addonsEnabled(),
  }));

  startCheckout = injectMutation(() => ({
    mutationFn: (priceId: string) =>
      lastValueFrom(
        this.#http.get<{ id: string; url: string }>(
          `${environment.baseUrl}/payments/checkout`,
          { params: { planId: priceId } },
        ),
      ),
  }));

  startAddonCheckout = injectMutation(() => ({
    mutationFn: (code: string) =>
      lastValueFrom(
        this.#http.post<{ id: string; url: string }>(
          `${environment.baseUrl}/payments/addons/${encodeURIComponent(code)}/checkout`,
          {},
        ),
      ),
  }));

  updateSettings = this.#userShared.updateSettings;

  createKid = injectMutation(() => ({
    mutationFn: (kid: AuthGen.User) =>
      lastValueFrom(
        this.#http.post<AuthGen.User>(`${environment.baseUrl}/user/kid`, kid),
      ),
    onSuccess: async () => {
      await this.#user.refetch();
      await this.invalidateFamilyKids();
    },
  }));

  verifyKid = injectMutation(() => ({
    mutationFn: (kidId: string) =>
      lastValueFrom(
        this.#http.patch<{ kidId: string }>(
          `${environment.baseUrl}/user/kid/verify`,
          {
            kidId,
          },
        ),
      ),
    onSuccess: async () => {
      await this.#user.refetch();
      await this.invalidateFamilyKids();
    },
  }));

  createParentLinkRequest = injectMutation(() => ({
    mutationFn: (dto: AuthGen.CreateParentLinkRequestDto) =>
      lastValueFrom(
        this.#http.post<AuthGen.ParentLinkRequestResponse>(
          `${environment.baseUrl}/user/parent-links`,
          dto,
        ),
      ),
  }));

  confirmDuty = injectMutation(() => ({
    mutationFn: (id: string) =>
      lastValueFrom(
        this.#http.patch<ChallengeGen.Dutie>(
          `${environment.baseUrl}/duties/confirm`,
          {
            dutyId: id,
          },
        ),
      ),
    onSuccess: async () => {
      await this.#user.refetch();
      await this.invalidateFamilyKids();
      this.#homeData.invalidateParentDuties();
    },
  }));

  createDutiesBatch = injectMutation(() => ({
    mutationFn: async (duties: CreateDutyPayload[]) => {
      const promises = duties.map((duty) =>
        lastValueFrom(
          this.#http.post<{ duty: ChallengeGen.Dutie }>(
            `${environment.baseUrl}/duties`,
            duty,
          ),
        ),
      );
      return Promise.all(promises);
    },
    onSuccess: async () => {
      await this.#user.refetch();
      await this.invalidateFamilyKids();
      this.#homeData.invalidateParentDuties();
    },
  }));

  updateDuty = injectMutation(() => ({
    mutationFn: (payload: UpdateDutyPayload) =>
      lastValueFrom(
        this.#http.patch<ChallengeGen.Dutie>(
          `${environment.baseUrl}/duties/update`,
          payload,
        ),
      ),
    onSuccess: async () => {
      await this.#user.refetch();
      await this.invalidateFamilyKids();
      this.#homeData.invalidateParentDuties();
    },
  }));

  cancelDuty = injectMutation(() => ({
    mutationFn: (dutyId: string) =>
      lastValueFrom(
        this.#http.patch<ChallengeGen.Dutie>(
          `${environment.baseUrl}/duties/cancel`,
          { dutyId },
        ),
      ),
    onSuccess: async () => {
      await this.#user.refetch();
      await this.invalidateFamilyKids();
      this.#homeData.invalidateParentDuties();
    },
  }));

  completeParentDutyPart = injectMutation(() => ({
    mutationFn: (dutyId: string) =>
      lastValueFrom(
        this.#http.patch<ChallengeGen.Dutie>(
          `${environment.baseUrl}/duties/parent-part`,
          { dutyId },
        ),
      ),
    onSuccess: () => {
      this.#homeData.invalidateParentDuties();
    },
  }));
}
